import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  CheckCircleIcon,
  StarIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

const TeacherProfileCompletePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const profileData = location.state || {};

  console.log('Teacher profile completion page loaded with data:', profileData);

  const handleGoToDashboard = () => {
    console.log('Navigating to teacher dashboard from completion page');
    navigate('/teacher-dashboard');
  };

  const handleViewProfile = () => {
    console.log('Viewing teacher profile from completion page');
    navigate('/teacher-profile', { state: { teacherData: profileData } });
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full">
          <div className="bg-slate-800 p-8 rounded-xl shadow-2xl text-center">
            
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-500/20 mb-6">
              <CheckCircleIcon className="h-12 w-12 text-green-400" />
            </div>

            {/* Header */}
            <h1 className="text-3xl font-bold text-white mb-4">
              Welcome to LessonLynx! 🎉
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Your teacher profile is now complete and ready for students!
            </p>

            {/* Success Message */}
            <div className="bg-green-500/10 border border-green-500/30 p-6 rounded-lg mb-8">
              <div className="text-center">
                <h3 className="text-green-300 font-semibold text-lg mb-2">
                  Profile Successfully Created
                </h3>
                <p className="text-green-200 text-sm">
                  Students can now discover your profile and book lessons with you. 
                  You'll receive notifications when students show interest.
                </p>
              </div>
            </div>

            {/* Next Steps */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-6">What happens next?</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Step 1 */}
                <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-yellow-500/20 p-2 rounded-full">
                      <StarIcon className="h-5 w-5 text-yellow-400" />
                    </div>
                    <h4 className="text-slate-200 font-medium">Get Discovered</h4>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Students browsing for teachers in your area and specialties will see your profile.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-blue-500/20 p-2 rounded-full">
                      <UserGroupIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <h4 className="text-slate-200 font-medium">Connect with Students</h4>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Receive booking requests and messages from interested students.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-green-500/20 p-2 rounded-full">
                      <CalendarDaysIcon className="h-5 w-5 text-green-400" />
                    </div>
                    <h4 className="text-slate-200 font-medium">Schedule Lessons</h4>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Use your dashboard to manage your schedule and confirm lesson bookings.
                  </p>
                </div>

                {/* Step 4 */}
                <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-purple-500/20 p-2 rounded-full">
                      <AcademicCapIcon className="h-5 w-5 text-purple-400" />
                    </div>
                    <h4 className="text-slate-200 font-medium">Start Teaching</h4>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Begin teaching and earning money while helping students achieve their musical goals.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={handleGoToDashboard}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold py-4 px-6 rounded-lg transition-colors text-lg"
              >
                Go to Teacher Dashboard
              </button>
              
              <button
                onClick={handleViewProfile}
                className="w-full border border-slate-600 text-slate-300 hover:text-slate-100 hover:border-slate-500 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Preview Your Profile
              </button>
            </div>

            {/* Additional Info */}
            <div className="mt-8 pt-6 border-t border-slate-600">
              <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0">
                    💡
                  </div>
                  <div className="text-left">
                    <p className="text-blue-300 text-sm font-medium">Pro Tip:</p>
                    <p className="text-blue-200 text-xs mt-1">
                      Complete your availability schedule in your dashboard to start receiving lesson requests immediately. 
                      The more detailed your profile, the better we can match you with compatible students!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6">
              <p className="text-xs text-slate-500">
                Need help getting started? Check out our <a href="/help" className="text-yellow-400 hover:text-yellow-300">Help Center</a> 
                {' '}or <a href="/contact" className="text-yellow-400 hover:text-yellow-300">contact support</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default TeacherProfileCompletePage; 